import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Lock, Shield, FileText, ChevronUp, Send, CheckCircle, Apple, Smartphone, Youtube } from 'lucide-react';

const XIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setEmail('');
      }, 3000);
    }
  };

  const responsiveStyles = getResponsiveStyles(isMobile, isSmallMobile);

  return (
    <footer style={responsiveStyles.footer}>
      <div style={responsiveStyles.decorativeCircle1} className="decorative-circle-1"></div>
      <div style={responsiveStyles.decorativeCircle2} className="decorative-circle-2"></div>
      <div style={responsiveStyles.decorativeCircle3} className="decorative-circle-3"></div>

      <div style={responsiveStyles.container}>
        <div style={responsiveStyles.newsletterSection} className="newsletter-section">
          <div style={responsiveStyles.newsletterContent}>
            <div style={responsiveStyles.newsletterLeft}>
              <h3 style={responsiveStyles.newsletterTitle}>Stay Updated with Health Tips 💌</h3>
              <p style={responsiveStyles.newsletterSubtitle}>Join 10,000+ subscribers getting weekly health insights and updates</p>
            </div>
            <form onSubmit={handleSubscribe} style={responsiveStyles.newsletterForm}>
              <div style={responsiveStyles.inputWrapper}>
                <Mail size={isSmallMobile ? 18 : 20} style={responsiveStyles.inputIcon} />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={responsiveStyles.emailInput}
                  className="email-input"
                  required
                />
              </div>
              <button type="submit" style={responsiveStyles.subscribeButton} className="subscribe-button">
                {subscribed ? (
                  <>
                    <CheckCircle size={isSmallMobile ? 18 : 20} />
                    <span>Subscribed!</span>
                  </>
                ) : (
                  <>
                    <Send size={isSmallMobile ? 18 : 20} />
                    <span>Subscribe</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <div style={responsiveStyles.grid} className="footer-grid">
          {/* Company Info */}
          <div style={styles.column}>
            <div style={styles.logoContainer}>
              <img 
                src="https://customer-assets.emergentagent.com/job_ehealth-portal/artifacts/aqa5vp4n_Frame%201707479730.png" 
                alt="Gewo Health" 
                style={styles.logoImage}
              />
              <div style={styles.logoGlow}></div>
            </div>
            <p style={styles.description}>
              Your trusted partner for online medical consultations and clinic management.
              Quality healthcare made accessible.
            </p>
            
            {/* Social Media */}
            <div style={styles.socialSection}>
              <h5 style={styles.socialTitle}>Follow Us</h5>
              <div style={styles.social}>
                <a href="#" style={styles.socialLink} className="social-link facebook-link" aria-label="Facebook">
                  <Facebook size={20} />
                </a>
                <a href="https://x.com/GewoHealth" target="_blank" rel="noopener noreferrer" style={styles.socialLink} className="social-link twitter-link" aria-label="X (formerly Twitter)">
                  <XIcon size={18} />
                </a>
                <a href="https://www.instagram.com/gewohealth" target="_blank" rel="noopener noreferrer" style={styles.socialLink} className="social-link instagram-link" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
                <a href="#" style={styles.socialLink} className="social-link linkedin-link" aria-label="LinkedIn">
                  <Linkedin size={20} />
                </a>
                <a href="https://www.youtube.com/@GewoHealth" target="_blank" rel="noopener noreferrer" style={styles.socialLink} className="social-link youtube-link" aria-label="YouTube">
                  <Youtube size={20} />
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div style={styles.column}>
            <h4 style={styles.columnTitle}>
              <span style={styles.titleUnderline}></span>
              Quick Links
            </h4>
            <ul style={styles.list}>
              <li><button onClick={() => scrollToSection('home')} style={styles.link} className="footer-link">Home</button></li>
              <li><button onClick={() => scrollToSection('specializations')} style={styles.link} className="footer-link">Specializations</button></li>
              <li><button onClick={() => scrollToSection('blog')} style={styles.link} className="footer-link">Blog</button></li>
              <li><button onClick={() => scrollToSection('app')} style={styles.link} className="footer-link">Download App</button></li>
            </ul>
          </div>

          {/* Services */}
          <div style={styles.column}>
            <h4 style={styles.columnTitle}>
              <span style={styles.titleUnderline}></span>
              Services
            </h4>
            <ul style={styles.list}>
              <li><a href="#" style={styles.link} className="footer-link">Online Consultation</a></li>
              <li><a href="#" style={styles.link} className="footer-link">Clinic Management</a></li>
              <li><a href="#" style={styles.link} className="footer-link">Health Records</a></li>
              <li><a href="#" style={styles.link} className="footer-link">Prescription Services</a></li>
            </ul>
          </div>

          {/* Contact & Download */}
          <div style={styles.column}>
            <h4 style={styles.columnTitle}>
              <span style={styles.titleUnderline}></span>
              Contact Us
            </h4>
            <ul style={styles.contactList}>
              <li style={styles.contactItem}>
                <div style={styles.contactIconWrapper}>
                  <Mail size={16} />
                </div>
                <span>contact@gewohealth.com</span>
              </li>
              <li style={styles.contactItem}>
                <div style={styles.contactIconWrapper}>
                  <Phone size={16} />
                </div>
                <span>+1 (555) 123-4567</span>
              </li>
              <li style={styles.contactItem}>
                <div style={styles.contactIconWrapper}>
                  <MapPin size={16} />
                </div>
                <span>123 Healthcare Ave, Medical District</span>
              </li>
            </ul>
            
            {/* App Download Badges */}
            <div style={styles.appBadgesSection}>
              <h5 style={styles.appBadgesTitle}>Download Our App</h5>
              <div style={styles.appBadges}>
                <a href="#" style={styles.appBadge} className="app-badge app-store-badge" aria-label="Download on App Store">
                  <Apple size={24} />
                  <div style={styles.appBadgeText}>
                    <span style={styles.appBadgeSmall}>Download on the</span>
                    <span style={styles.appBadgeLarge}>App Store</span>
                  </div>
                </a>
                <a href="#" style={styles.appBadge} className="app-badge google-play-badge" aria-label="Get it on Google Play">
                  <Smartphone size={24} />
                  <div style={styles.appBadgeText}>
                    <span style={styles.appBadgeSmall}>Get it on</span>
                    <span style={styles.appBadgeLarge}>Google Play</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div style={responsiveStyles.trustBadgesSection}>
          <div style={styles.trustBadge} className="trust-badge">
            <Shield size={24} />
            <div style={styles.trustBadgeText}>
              <span style={styles.trustBadgeTitle}>HIPAA Compliant</span>
              <span style={styles.trustBadgeSubtitle}>Secure & Private <span style={styles.inProcessBadge}>(In Process)</span></span>
            </div>
          </div>
          <div style={styles.trustBadgeDivider}></div>
          <div style={styles.trustBadge} className="trust-badge">
            <CheckCircle size={24} />
            <div style={styles.trustBadgeText}>
              <span style={styles.trustBadgeTitle}>ISO 27001 Certified</span>
              <span style={styles.trustBadgeSubtitle}>Data Protection <span style={styles.inProcessBadge}>(In Process)</span></span>
            </div>
          </div>
          <div style={styles.trustBadgeDivider}></div>
          <div style={styles.trustBadge} className="trust-badge">
            <Lock size={24} />
            <div style={styles.trustBadgeText}>
              <span style={styles.trustBadgeTitle}>Verified Healthcare</span>
              <span style={styles.trustBadgeSubtitle}>Licensed Providers</span>
            </div>
          </div>
          <div style={styles.trustBadgeDivider}></div>
          <a href="/security" style={styles.securityVerifiedBadge} className="security-verified-badge">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={styles.securityVerifiedIcon}>
              {/* Shield outline */}
              <path d="M12 2L4 6V11C4 16 7 20 12 22C17 20 20 16 20 11V6L12 2Z" 
                    stroke="#133E97" 
                    strokeWidth="2" 
                    strokeLinejoin="round"
                    fill="none"/>
              {/* Checkmark */}
              <path d="M9 12L11 14L15 10" 
                    stroke="#133E97" 
                    strokeWidth="2.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    fill="none"/>
            </svg>
            <span style={styles.securityVerifiedText}>Security Verified</span>
          </a>
        </div>

        <div style={responsiveStyles.bottom}>
          <p style={styles.copyright}>
            © {new Date().getFullYear()} Gewo Health. All rights reserved.
          </p>
          <div style={styles.legal}>
            <a href="/legal" style={styles.legalLink} className="legal-link">Privacy Policy</a>
            <span style={styles.separator}>•</span>
            <a href="/legal" style={styles.legalLink} className="legal-link">Terms of Service</a>
            <span style={styles.separator}>•</span>
            <a href="/legal" style={styles.legalLink} className="legal-link">Cookie Policy</a>
          </div>
        </div>
      </div>

      <button 
        onClick={scrollToTop} 
        style={responsiveStyles.backToTop} 
        className="back-to-top-button"
        aria-label="Back to top"
      >
        <ChevronUp size={isSmallMobile ? 20 : 24} />
      </button>
    </footer>
  );
};

const getResponsiveStyles = (isMobile, isSmallMobile) => ({
  footer: {
    background: 'linear-gradient(135deg, #0A1E4D 0%, #0D2C6E 30%, #133E97 70%, #1a4bb8 100%)',
    color: 'white',
    padding: '0 0 24px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.1)'
  },
  decorativeCircle1: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(244, 206, 123, 0.08) 0%, transparent 70%)',
    top: '-200px',
    right: '-100px',
    pointerEvents: 'none'
  },
  decorativeCircle2: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(19, 62, 151, 0.15) 0%, transparent 70%)',
    bottom: '100px',
    left: '-150px',
    pointerEvents: 'none'
  },
  decorativeCircle3: {
    position: 'absolute',
    width: '250px',
    height: '250px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(244, 206, 123, 0.06) 0%, transparent 70%)',
    top: '50%',
    right: '20%',
    pointerEvents: 'none'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isSmallMobile ? '0 16px' : isMobile ? '0 20px' : '0 24px',
    position: 'relative',
    zIndex: 1
  },
  newsletterSection: {
    background: 'linear-gradient(135deg, rgba(244, 206, 123, 0.1) 0%, rgba(244, 206, 123, 0.05) 100%)',
    borderRadius: isSmallMobile ? '16px' : '20px',
    padding: isSmallMobile ? '24px 20px' : isMobile ? '32px 24px' : '48px',
    marginBottom: isSmallMobile ? '40px' : isMobile ? '48px' : '60px',
    marginTop: isSmallMobile ? '40px' : isMobile ? '48px' : '60px',
    border: '1px solid rgba(244, 206, 123, 0.2)',
    backdropFilter: 'blur(10px)'
  },
  newsletterContent: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'center' : 'center',
    gap: isSmallMobile ? '20px' : isMobile ? '24px' : '32px',
    flexWrap: 'wrap',
    textAlign: isMobile ? 'center' : 'left'
  },
  newsletterLeft: {
    flex: '1',
    minWidth: isMobile ? '100%' : '280px',
    textAlign: isMobile ? 'center' : 'left'
  },
  newsletterTitle: {
    fontSize: isSmallMobile ? '20px' : isMobile ? '24px' : '28px',
    fontWeight: '700',
    color: 'white',
    marginBottom: '8px',
    lineHeight: '1.3'
  },
  newsletterSubtitle: {
    fontSize: isSmallMobile ? '13px' : '15px',
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: '1.5'
  },
  newsletterForm: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isSmallMobile ? '10px' : '12px',
    flex: '1',
    minWidth: isMobile ? '100%' : '320px',
    maxWidth: isMobile ? '100%' : '500px',
    width: isMobile ? '100%' : 'auto'
  },
  inputWrapper: {
    position: 'relative',
    flex: '1',
    display: 'flex',
    alignItems: 'center'
  },
  inputIcon: {
    position: 'absolute',
    left: '16px',
    color: 'rgba(19, 62, 151, 0.5)',
    pointerEvents: 'none'
  },
  emailInput: {
    width: '100%',
    padding: isSmallMobile ? '12px 14px 12px 44px' : '14px 16px 14px 48px',
    borderRadius: '12px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    background: 'rgba(255, 255, 255, 0.95)',
    fontSize: isSmallMobile ? '14px' : '15px',
    color: '#133E97',
    outline: 'none',
    transition: 'all 0.3s ease'
  },
  subscribeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: isSmallMobile ? '12px 24px' : '14px 28px',
    width: isMobile ? '100%' : 'auto',
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    color: '#133E97',
    border: 'none',
    borderRadius: '12px',
    fontSize: isSmallMobile ? '14px' : '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(244, 206, 123, 0.3)'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: isSmallMobile ? '32px' : isMobile ? '40px' : '48px',
    marginBottom: isSmallMobile ? '32px' : isMobile ? '40px' : '48px'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: isSmallMobile ? '16px' : '20px',
    textAlign: isMobile ? 'center' : 'left',
    alignItems: isMobile ? 'center' : 'flex-start'
  },
  columnTitle: {
    fontSize: isSmallMobile ? '16px' : isMobile ? '17px' : '18px',
    fontWeight: '700',
    color: 'white',
    marginBottom: '4px',
    position: 'relative',
    paddingBottom: '12px',
    letterSpacing: '0.3px',
    textAlign: isMobile ? 'center' : 'left'
  },
  titleUnderline: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '40px',
    height: '3px',
    background: 'linear-gradient(90deg, #F4CE7B 0%, transparent 100%)',
    borderRadius: '2px'
  },
  logoContainer: {
    position: 'relative',
    display: 'inline-block',
    marginBottom: '12px'
  },
  logoImage: {
    height: isSmallMobile ? '50px' : isMobile ? '60px' : '70px',
    width: 'auto',
    filter: 'brightness(0) invert(1) drop-shadow(0 4px 8px rgba(244, 206, 123, 0.3))',
    marginBottom: '0',
    position: 'relative',
    zIndex: 1,
    transition: 'all 0.3s ease'
  },
  logoGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '120%',
    height: '120%',
    background: 'radial-gradient(circle, rgba(244, 206, 123, 0.15) 0%, transparent 70%)',
    borderRadius: '50%',
    zIndex: 0,
    pointerEvents: 'none'
  },
  description: {
    fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
    lineHeight: '1.7',
    color: 'rgba(255, 255, 255, 0.85)',
    textAlign: isMobile ? 'center' : 'left'
  },
  
  // Social Media
  socialSection: {
    marginTop: '8px'
  },
  socialTitle: {
    fontSize: isSmallMobile ? '13px' : '14px',
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: '12px',
    letterSpacing: '0.5px',
    textAlign: isMobile ? 'center' : 'left'
  },
  social: {
    display: 'flex',
    gap: isSmallMobile ? '10px' : '12px',
    justifyContent: isMobile ? 'center' : 'flex-start',
    flexWrap: 'wrap'
  },
  socialLink: {
    width: '44px',
    height: '44px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    textDecoration: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  
  // Links
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: isSmallMobile ? '10px' : '12px',
    alignItems: isMobile ? 'center' : 'flex-start',
    width: isMobile ? '100%' : 'auto'
  },
  link: {
    fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
    color: 'rgba(255, 255, 255, 0.85)',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    background: 'none',
    border: 'none',
    padding: '4px 0',
    cursor: 'pointer',
    textAlign: isMobile ? 'center' : 'left',
    position: 'relative',
    display: 'inline-block',
    width: isMobile ? '100%' : 'fit-content'
  },
  
  // Contact
  contactList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: isSmallMobile ? '12px' : '16px',
    alignItems: isMobile ? 'center' : 'flex-start',
    width: isMobile ? '100%' : 'auto'
  },
  contactItem: {
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start',
    fontSize: isSmallMobile ? '13px' : '15px',
    color: 'rgba(255, 255, 255, 0.85)',
    lineHeight: '1.6',
    justifyContent: isMobile ? 'center' : 'flex-start',
    width: isMobile ? '100%' : 'auto'
  },
  contactIconWrapper: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'rgba(244, 206, 123, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#F4CE7B',
    flexShrink: 0
  },
  
  // App Badges
  appBadgesSection: {
    marginTop: '24px'
  },
  appBadgesTitle: {
    fontSize: isSmallMobile ? '13px' : '14px',
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: '12px',
    letterSpacing: '0.5px',
    textAlign: isMobile ? 'center' : 'left'
  },
  appBadges: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: isMobile ? '100%' : 'auto'
  },
  appBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: isMobile ? 'center' : 'flex-start',
    gap: '12px',
    padding: isSmallMobile ? '10px 14px' : '10px 16px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    textDecoration: 'none',
    color: 'white',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    width: isMobile ? '100%' : 'auto'
  },
  appBadgeText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  appBadgeSmall: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.7)',
    lineHeight: '1'
  },
  appBadgeLarge: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'white',
    lineHeight: '1.2'
  },
  
  // Trust Badges
  trustBadgesSection: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'center' : 'center',
    gap: isSmallMobile ? '16px' : isMobile ? '20px' : '24px',
    padding: isSmallMobile ? '24px 16px' : isMobile ? '28px 20px' : '32px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: isSmallMobile ? '12px' : '16px',
    marginBottom: isSmallMobile ? '32px' : isMobile ? '40px' : '48px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    flexWrap: 'wrap'
  },
  trustBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: isMobile ? 'center' : 'flex-start',
    gap: '14px',
    flex: isMobile ? 'none' : '1',
    minWidth: isMobile ? 'auto' : '200px',
    width: isMobile ? '100%' : 'auto',
    color: 'white',
    textDecoration: 'none'
  },
  trustBadgeText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  trustBadgeTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'white',
    lineHeight: '1.2'
  },
  trustBadgeSubtitle: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.7)',
    lineHeight: '1.3',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center'
  },
  inProcessBadge: {
    fontSize: '11px',
    color: '#FFD98E',
    fontWeight: '500',
    fontStyle: 'italic'
  },
  trustBadgeDivider: {
    width: isMobile ? '100%' : '1px',
    height: isMobile ? '1px' : '50px',
    background: 'rgba(255, 255, 255, 0.15)',
    display: isMobile ? 'block' : 'block'
  },
  
  // Security Verified Badge (matching reference image)
  securityVerifiedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isSmallMobile ? '10px' : '12px',
    padding: isSmallMobile ? '12px 24px' : isMobile ? '14px 26px' : '14px 28px',
    background: 'linear-gradient(180deg, #E8C068 0%, #D4A84E 100%)',
    borderRadius: '50px',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 0 rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
    border: 'none',
    minWidth: isMobile ? 'auto' : '200px',
    width: isMobile ? '100%' : 'auto'
  },
  securityVerifiedIcon: {
    flexShrink: 0,
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
  },
  securityVerifiedText: {
    fontSize: isSmallMobile ? '14px' : isMobile ? '15px' : '16px',
    fontWeight: '700',
    color: '#133E97',
    letterSpacing: '0.3px',
    textShadow: '0 1px 1px rgba(255, 255, 255, 0.3)'
  },
  bottom: {
    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
    paddingTop: isSmallMobile ? '20px' : isMobile ? '24px' : '28px',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'center' : 'center',
    flexWrap: 'wrap',
    gap: isSmallMobile ? '16px' : '20px',
    textAlign: isMobile ? 'center' : 'left'
  },
  copyright: {
    fontSize: isSmallMobile ? '12px' : '14px',
    color: 'rgba(255, 255, 255, 0.7)',
    margin: 0,
    letterSpacing: '0.3px'
  },
  legal: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isSmallMobile ? '12px' : isMobile ? '16px' : '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: isMobile ? 'center' : 'flex-start'
  },
  legalLink: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.75)',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    position: 'relative'
  },
  separator: {
    color: 'rgba(255, 255, 255, 0.3)'
  },
  
  // Back to Top Button
  backToTop: {
    position: 'fixed',
    bottom: isSmallMobile ? '20px' : isMobile ? '24px' : '32px',
    right: isSmallMobile ? '20px' : isMobile ? '24px' : '32px',
    width: isSmallMobile ? '48px' : isMobile ? '50px' : '52px',
    height: isSmallMobile ? '48px' : isMobile ? '50px' : '52px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
    color: '#133E97',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(244, 206, 123, 0.3)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 999
  }
});

const styles = getResponsiveStyles(false, false);

// Add enhanced styles and animations
const footerStyleSheet = document.createElement('style');
footerStyleSheet.textContent = `
  /* Newsletter Input Focus */
  .email-input:focus {
    border-color: #F4CE7B !important;
    box-shadow: 0 0 0 3px rgba(244, 206, 123, 0.2) !important;
  }
  
  /* Subscribe Button Hover */
  .subscribe-button:hover {
    background: linear-gradient(135deg, #FFD98E 0%, #F4CE7B 100%) !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(244, 206, 123, 0.4) !important;
  }
  
  .subscribe-button:active {
    transform: translateY(0);
  }
  
  /* Footer Links with Smooth Underline */
  .footer-link::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #F4CE7B 0%, #FFD98E 100%);
    transition: width 0.3s ease;
  }
  
  .footer-link:hover::after {
    width: 100%;
  }
  
  .footer-link:hover {
    color: #F4CE7B !important;
    padding-left: 4px;
  }
  
  /* Social Links with Brand Colors */
  .facebook-link:hover {
    background: rgba(59, 89, 152, 0.3) !important;
    border-color: rgba(59, 89, 152, 0.5) !important;
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 8px 16px rgba(59, 89, 152, 0.3);
  }
  
  .twitter-link:hover {
    background: rgba(29, 161, 242, 0.3) !important;
    border-color: rgba(29, 161, 242, 0.5) !important;
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 8px 16px rgba(29, 161, 242, 0.3);
  }
  
  .instagram-link:hover {
    background: linear-gradient(135deg, rgba(131, 58, 180, 0.3), rgba(253, 29, 29, 0.3), rgba(252, 176, 69, 0.3)) !important;
    border-color: rgba(253, 29, 29, 0.5) !important;
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 8px 16px rgba(253, 29, 29, 0.3);
  }
  
  .linkedin-link:hover {
    background: rgba(0, 119, 181, 0.3) !important;
    border-color: rgba(0, 119, 181, 0.5) !important;
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 8px 16px rgba(0, 119, 181, 0.3);
  }
  
  /* App Badges Hover */
  .app-store-badge:hover {
    background: rgba(255, 255, 255, 0.2) !important;
    border-color: rgba(255, 255, 255, 0.3) !important;
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }
  
  .google-play-badge:hover {
    background: rgba(52, 168, 83, 0.2) !important;
    border-color: rgba(52, 168, 83, 0.4) !important;
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(52, 168, 83, 0.3);
  }
  
  /* Trust Badge Hover */
  .trust-badge:hover {
    transform: translateY(-2px);
  }
  
  /* Security Verified Badge Hover (matching reference image style) */
  .security-verified-badge:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 6px 20px rgba(232, 192, 104, 0.4), 
                inset 0 2px 0 rgba(255, 255, 255, 0.4), 
                inset 0 -2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .security-verified-badge:active {
    transform: translateY(-1px) scale(1.01);
    box-shadow: 0 3px 12px rgba(232, 192, 104, 0.3), 
                inset 0 2px 0 rgba(255, 255, 255, 0.3), 
                inset 0 -1px 2px rgba(0, 0, 0, 0.2);
  }
  
  /* Legal Links Hover with Underline */
  .legal-link::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: #F4CE7B;
    transition: width 0.3s ease;
  }
  
  .legal-link:hover::after {
    width: 100%;
  }
  
  .legal-link:hover {
    color: #F4CE7B !important;
  }
  
  /* Back to Top Button */
  .back-to-top-button:hover {
    transform: translateY(-4px) scale(1.1);
    box-shadow: 0 12px 32px rgba(244, 206, 123, 0.5);
  }
  
  .back-to-top-button:active {
    transform: translateY(-2px) scale(1.05);
  }
  
  /* Logo Enhancement */
  .footer-grid > div:first-child img:hover {
    filter: brightness(0) invert(1) drop-shadow(0 6px 16px rgba(244, 206, 123, 0.5)) !important;
    transform: scale(1.05);
  }
  
  /* Decorative Circles Animation */
  .decorative-circle-1 {
    animation: float 8s ease-in-out infinite;
  }
  
  .decorative-circle-2 {
    animation: float 10s ease-in-out infinite reverse;
  }
  
  .decorative-circle-3 {
    animation: float 12s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0) translateX(0);
    }
    25% {
      transform: translateY(-20px) translateX(10px);
    }
    50% {
      transform: translateY(0) translateX(-10px);
    }
    75% {
      transform: translateY(20px) translateX(5px);
    }
  }
  
  /* Newsletter Section Animation */
  .newsletter-section {
    animation: fadeInUp 0.8s ease-out;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Responsive Design */
  @media (max-width: 768px) {
    .footer-grid {
      grid-template-columns: 1fr !important;
      gap: 40px !important;
      text-align: center;
    }
    
    .newsletter-section {
      padding: 32px 24px !important;
      text-align: center;
    }
    
    .newsletter-content {
      flex-direction: column !important;
      align-items: center !important;
    }
    
    .newsletter-form {
      flex-direction: column !important;
      width: 100%;
    }
    
    .subscribe-button {
      width: 100%;
      justify-content: center;
    }
    
    .trust-badges-section {
      flex-direction: column !important;
      gap: 20px !important;
    }
    
    .trust-badge-divider {
      display: none;
    }
    
    .back-to-top-button {
      right: 20px !important;
      bottom: 20px !important;
      width: 48px !important;
      height: 48px !important;
    }
  }
  
  @media (max-width: 480px) {
    .newsletter-section {
      padding: 24px 20px !important;
    }
    
    .newsletter-title {
      font-size: 22px !important;
    }
    
    .app-badges {
      width: 100%;
    }
    
    .app-badge {
      justify-content: center;
    }
  }
`;

// Only append if not already added
if (!document.getElementById('footer-enhanced-styles')) {
  footerStyleSheet.id = 'footer-enhanced-styles';
  document.head.appendChild(footerStyleSheet);
}

export default Footer;